import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import { Sparkles, BarChart3, Target } from 'lucide-react';
import type { V2Features } from '@halo/web-types';

interface HaloSettingsWithFeatures {
  storeId: number;
  cpmRate: number;
  v2Features: V2Features;
}

type FeatureKey = keyof V2Features;

interface FeatureInfo {
  readonly key: FeatureKey;
  readonly label: string;
  readonly description: string;
}

const ANALYTICS_FEATURES: readonly FeatureInfo[] = [
  { key: 'revenueWaterfall', label: 'Revenue Waterfall', description: 'Visual breakdown of channel contributions' },
  { key: 'periodComparison', label: 'Period Comparison', description: 'Side-by-side time period comparison' },
  { key: 'revenueForecast', label: 'Revenue Forecast', description: 'Predictive revenue modeling' },
  { key: 'weeklySummary', label: 'Weekly Summary', description: 'Auto-generated performance insights' },
  { key: 'anomalyAlerts', label: 'Anomaly Alerts', description: 'Unusual pattern detection' },
] as const;

const ATTRIBUTION_FEATURES: readonly FeatureInfo[] = [
  { key: 'campaignAttribution', label: 'Campaign Attribution', description: 'Campaign-level drill-down' },
  { key: 'creativeHaloLeaderboard', label: 'Creative Leaderboard', description: 'Cross-platform creative impact' },
  { key: 'marginalRoasCurve', label: 'Marginal ROAS Curve', description: 'Diminishing returns analysis' },
  { key: 'productHaloIndex', label: 'Product Halo Index', description: 'Product advertising benefit' },
  { key: 'budgetSimulator', label: 'Budget Simulator', description: 'Spend change projections' },
  { key: 'multiSignalAttribution', label: 'Multi-Signal Attribution', description: 'Multi-signal combined attribution' },
] as const;

const ALL_FEATURES = [...ANALYTICS_FEATURES, ...ATTRIBUTION_FEATURES];

const DEFAULT_FEATURES: V2Features = {
  revenueWaterfall: false,
  campaignAttribution: false,
  budgetSimulator: false,
  periodComparison: false,
  creativeHaloLeaderboard: false,
  marginalRoasCurve: false,
  revenueForecast: false,
  weeklySummary: false,
  productHaloIndex: false,
  anomalyAlerts: false,
  multiSignalAttribution: false,
};

export function V2FeaturesSection({ storeId }: { readonly storeId: number }) {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['admin', 'halo-settings', storeId],
    queryFn: async ({ signal }) => {
      const res = await api.get<HaloSettingsWithFeatures>(
        `/api/v1/analytics/halo-settings?storeId=${storeId}`,
        { signal },
      );
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load settings');
      return res.data;
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ key, value }: { key: FeatureKey; value: boolean }) => {
      const res = await api.patch<HaloSettingsWithFeatures>(
        `/api/v1/analytics/halo-settings?storeId=${storeId}`,
        { v2Features: { [key]: value } },
      );
      if (!res.success) throw new Error(res.error ?? 'Failed to update feature');
      return res.data;
    },
    onMutate: async ({ key, value }) => {
      await queryClient.cancelQueries({ queryKey: ['admin', 'halo-settings', storeId] });
      const previous = queryClient.getQueryData<HaloSettingsWithFeatures>(
        ['admin', 'halo-settings', storeId],
      );
      if (previous) {
        queryClient.setQueryData<HaloSettingsWithFeatures>(
          ['admin', 'halo-settings', storeId],
          { ...previous, v2Features: { ...previous.v2Features, [key]: value } },
        );
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['admin', 'halo-settings', storeId], context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'halo-settings', storeId] });
    },
  });

  const bulkMutation = useMutation({
    mutationFn: async (enableAll: boolean) => {
      const v2Features: Record<string, boolean> = {};
      for (const feat of ALL_FEATURES) {
        v2Features[feat.key] = enableAll;
      }
      const res = await api.patch<HaloSettingsWithFeatures>(
        `/api/v1/analytics/halo-settings?storeId=${storeId}`,
        { v2Features },
      );
      if (!res.success) throw new Error(res.error ?? 'Failed to update features');
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'halo-settings', storeId] });
    },
  });

  const features = settings?.v2Features ?? DEFAULT_FEATURES;

  const enabledCount = useMemo(
    () => ALL_FEATURES.filter((f) => features[f.key]).length,
    [features],
  );

  function handleToggle(key: FeatureKey) {
    toggleMutation.mutate({ key, value: !features[key] });
  }

  function renderFeatureGroup(
    title: string,
    icon: React.ReactNode,
    items: readonly FeatureInfo[],
  ) {
    return (
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          {icon}
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{title}</span>
        </div>
        <div className="space-y-1.5">
          {items.map((feat) => {
            const isOn = features[feat.key];
            return (
              <div
                key={feat.key}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer hover:shadow-sm ${
                  isOn
                    ? 'border-l-emerald-500 border-l-2 border-gray-200 dark:border-gray-800 bg-emerald-50/30'
                    : 'border-gray-100 hover:border-gray-200'
                }`}
                onClick={() => handleToggle(feat.key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleToggle(feat.key); }}
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={isOn}
                  onClick={(e) => { e.stopPropagation(); handleToggle(feat.key); }}
                  className="relative inline-flex h-5 w-10 flex-shrink-0 items-center rounded-full transition-colors"
                  style={{ backgroundColor: isOn ? '#10b981' : '#d1d5db' }}
                >
                  <span
                    className="inline-block h-3.5 w-3.5 rounded-full bg-white dark:bg-gray-900 shadow-sm transition-transform"
                    style={{ transform: isOn ? 'translateX(20px)' : 'translateX(2px)' }}
                  />
                </button>
                <div className="min-w-0">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 block">{feat.label}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 block">{feat.description}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-amber-50 dark:bg-amber-950/40 flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-amber-600" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">V2 Features</h3>
          <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
            {enabledCount} of {ALL_FEATURES.length} enabled
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => bulkMutation.mutate(true)}
            disabled={bulkMutation.isPending || enabledCount === ALL_FEATURES.length}
            className="px-2.5 py-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 disabled:opacity-40 transition-colors"
          >
            Enable All
          </button>
          <button
            onClick={() => bulkMutation.mutate(false)}
            disabled={bulkMutation.isPending || enabledCount === 0}
            className="px-2.5 py-1 text-[11px] font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors"
          >
            Disable All
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="animate-pulse space-y-3">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="h-14 bg-gray-100 dark:bg-gray-800 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-5">
          {renderFeatureGroup(
            'Analytics',
            <BarChart3 className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />,
            ANALYTICS_FEATURES,
          )}
          {renderFeatureGroup(
            'Attribution',
            <Target className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />,
            ATTRIBUTION_FEATURES,
          )}
        </div>
      )}
    </div>
  );
}
